// ============================================================================
// crmPreferencias · a Aparência do CRM, por pessoa (28/10/2026, CRM_APARENCIA)
// ============================================================================
// Ideia do dono: "colocar coisas de customização, que elas possam customizar o
// CRM da forma que elas queiram em relação à cor". Só uma conveniência deste
// navegador (localStorage fk.crm.prefs.v1.<pessoa>): nada vai ao servidor, a
// tela funciona igual com o armazenamento bloqueado (fica o padrão).
// Toda leitura passa por uma lista fechada (valor estranho volta ao padrão) e
// o volume é limitado a 0..1. O azul do DNA é o padrão e a única cor que alguém
// vê sem escolher outra; as outras dependem do ok do dono.
// ============================================================================

import { useCallback, useEffect, useState } from 'react';

// Revisão de 28/10/2026: só as cores do DNA (azul e marinho) até o ok do dono. Verde-água, grafite e vinho
// ficam guardados aqui e no index.css; para religar uma, é só voltar o item para ACENTOS.
export const ACENTOS_AGUARDANDO_DONO = Object.freeze([
  { id: 'verde-agua', rotulo: 'Verde-água', cor: '#1E7A73' },
  { id: 'grafite', rotulo: 'Grafite', cor: '#475569' },
  { id: 'vinho', rotulo: 'Vinho', cor: '#7A2E3B' },
]);
export const ACENTOS = Object.freeze([
  { id: 'azul', rotulo: 'Azul DNA', cor: '#41659E' },
  { id: 'marinho', rotulo: 'Marinho', cor: '#1A2A42' },
]);
// fundo: liso por padrão (revisão de 28/10/2026: o dono pediu clean; a trama fica como opção na Aparência)
export const PREFS_PADRAO = Object.freeze({
  acento: 'azul', densidade: 'confortavel', texto: 'normal', fundo: false, som: true, volume: 0.25, notificar: false, fichaAberta: null,
});
const DENSIDADES = ['confortavel', 'compacta'];
const TEXTOS = ['normal', 'grande'];

export const chavePrefs = (euId) => `fk.crm.prefs.v1.${euId || 'anon'}`;

/** Só o que está na lista; o resto volta ao padrão. Volume em passos de 0,05 entre 0 e 1. */
export function validarPrefs(o) {
  const x = o && typeof o === 'object' && !Array.isArray(o) ? o : {};
  const vol = Number(x.volume);
  return {
    acento: ACENTOS.some(a => a.id === x.acento) ? x.acento : PREFS_PADRAO.acento,
    densidade: DENSIDADES.includes(x.densidade) ? x.densidade : PREFS_PADRAO.densidade,
    texto: TEXTOS.includes(x.texto) ? x.texto : PREFS_PADRAO.texto,
    fundo: typeof x.fundo === 'boolean' ? x.fundo : PREFS_PADRAO.fundo,
    som: typeof x.som === 'boolean' ? x.som : PREFS_PADRAO.som,
    volume: Number.isFinite(vol) ? Math.round(Math.max(0, Math.min(1, vol)) * 20) / 20 : PREFS_PADRAO.volume,
    notificar: typeof x.notificar === 'boolean' ? x.notificar : PREFS_PADRAO.notificar,
    fichaAberta: typeof x.fichaAberta === 'boolean' ? x.fichaAberta : null,
  };
}

function armazem() {
  try { return typeof window !== 'undefined' && window.localStorage ? window.localStorage : null; } catch { return null; }
}

export function lerPrefs(chave, storage = armazem()) {
  try {
    const bruto = storage ? storage.getItem(chave) : null;
    return validarPrefs(bruto ? JSON.parse(bruto) : null);
  } catch { return validarPrefs(null); }
}

export function gravarPrefs(chave, prefs, storage = armazem()) {
  try { if (storage) storage.setItem(chave, JSON.stringify(validarPrefs(prefs))); return true; } catch { return false; }
}

/** Os atributos da raiz do CRM (o index.css lê data-crm-*). */
export function atributosDaRaiz(p) {
  const x = validarPrefs(p);
  return { 'data-crm-acento': x.acento, 'data-crm-densidade': x.densidade, 'data-crm-texto': x.texto, 'data-crm-fundo': x.fundo ? '1' : '0' };
}

/** [prefs, mudar(parcial), voltarAoPadrao]; acompanha as outras abas pelo evento storage. */
export function useCrmPreferencias(euId, ligado = true) {
  const chave = chavePrefs(euId);
  const [prefs, setPrefs] = useState(() => (ligado ? lerPrefs(chave) : validarPrefs(null)));
  useEffect(() => { setPrefs(ligado ? lerPrefs(chave) : validarPrefs(null)); }, [chave, ligado]);
  useEffect(() => {
    if (!ligado || typeof window === 'undefined') return undefined;
    const f = (e) => { if (e.key === chave) setPrefs(lerPrefs(chave)); };
    window.addEventListener('storage', f);
    return () => window.removeEventListener('storage', f);
  }, [chave, ligado]);
  const mudar = useCallback((parcial) => {
    setPrefs(p => { const n = validarPrefs({ ...p, ...parcial }); if (ligado) gravarPrefs(chave, n); return n; });
  }, [chave, ligado]);
  const padrao = useCallback(() => {
    setPrefs(p => { const n = validarPrefs({ fichaAberta: p.fichaAberta }); if (ligado) gravarPrefs(chave, n); return n; });
  }, [chave, ligado]);
  return [prefs, mudar, padrao];
}
